import { apiRequest } from '../../../../core/services/apiClient';
import { AlertsResponse, AlertStats, AlertFeedback, Alert } from '../types';

const API_BASE_URL = 'http://localhost:8000';

const resolveSiteCode = (siteCode?: string) => {
    const fromArg = siteCode?.trim();
    if (fromArg) return fromArg;

    const fromSession = sessionStorage.getItem('tenant')?.trim();
    if (fromSession) return fromSession;

    const fromLocal = localStorage.getItem('currentSite')?.trim();
    if (fromLocal) return fromLocal;

    return '';
};

const getAuthHeaders = (siteCode?: string) => {
    const token = sessionStorage.getItem('access_token') ||
                  localStorage.getItem('access_token') ||
                  localStorage.getItem('token') ||
                  sessionStorage.getItem('token');
    const site = resolveSiteCode(siteCode);
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(site ? { 'X-Site-Code': site } : {})
    };
};

export const alertsApi = {
    /**
     * Fetch all alerts with optional site filtering
     */
    fetchAlerts: async (siteCode?: string, selectedDate?: string) => {
        const params: Record<string, string> = {
            page_size: '1000',
        };

        if (siteCode) {
            params.site_code = siteCode;
        }

        if (selectedDate) {
            params.date = selectedDate;
        }

        return apiRequest<AlertsResponse>(`${API_BASE_URL}/api/v1/alerts/`, {
            params,
            headers: getAuthHeaders(siteCode),
        });
    },

    /**
     * Fetch alert statistics
     */
    fetchStats: async (siteCode?: string, selectedDate?: string) => {
        const params: Record<string, string> = {};
        if (siteCode) {
            params.site_code = siteCode;
        }

        if (selectedDate) {
            params.date = selectedDate;
        }

        return apiRequest<AlertStats>(`${API_BASE_URL}/api/v1/alerts/stats/`, {
            params,
            headers: getAuthHeaders(siteCode),
        });
    },

    /**
     * Update an alert (feedback)
     */
    updateAlert: async (id: number, data: AlertFeedback, siteCode?: string) => {
        // Using the custom feedback endpoint which handles complex validation and warnings
        return apiRequest<Alert>(`${API_BASE_URL}/api/v1/alerts/${id}/feedback/`, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: getAuthHeaders(siteCode),
        });
    },

    /**
     * Generate alerts for a specific date (Pipeline)
     */
    generateAlerts: async (date: string, siteCode?: string) => {
        return apiRequest<{ success: boolean; message: string }>(`${API_BASE_URL}/api/v1/alerts/generate/${date}/`, {
            method: 'POST',
            headers: getAuthHeaders(siteCode),
        });
    },

    /**
     * Fetch spare parts for a specific fault category
     */
    fetchSpareParts: async (faultCategory: string, equipmentName?: string, siteCode?: string) => {
        const params: Record<string, string> = {
            fault_category: faultCategory,
        };

        if (equipmentName) {
            params.equipment_name = equipmentName;
        }

        if (siteCode) {
            params.site_code = siteCode;
        }

        return apiRequest<any[]>(`${API_BASE_URL}/api/v1/spare-parts/`, {
            params,
            headers: getAuthHeaders(siteCode),
        });
    },

    /**
     * Consume a spare part (decrement availability)
     */
    consumeSparePart: async (id: number, quantity: number = 1, action_type?: string, notes?: string, siteCode?: string) => {
        return apiRequest<any>(`${API_BASE_URL}/api/v1/spare-parts/${id}/consume/`, {
            method: 'POST',
            body: JSON.stringify({
                quantity,
                action_type,
                notes
            }),
            headers: getAuthHeaders(siteCode),
        });
    },

    /**
     * Fetch fault categories for a specific equipment
     */
    fetchFaultCategoriesByEquipment: async (equipment: string, siteCode?: string) => {
        return apiRequest<{ equipment: string; fault_categories: string[]; count: number }>(
            `${API_BASE_URL}/api/v1/fault-categories-by-equipment/${encodeURIComponent(equipment)}/`,
            {
                headers: getAuthHeaders(siteCode)
            }
        );
    },

    /**
     * Fetch distinct inventory categories for current site scope
     */
    fetchInventoryCategories: async (siteCode?: string) => {
        const params: Record<string, string> = {
            page_size: '2000',
        };

        if (siteCode) {
            params.site_code = siteCode;
        }

        const response = await apiRequest<any>(`${API_BASE_URL}/api/v1/inventory/`, {
            params,
            headers: getAuthHeaders(siteCode),
        });

        const rows = Array.isArray(response)
            ? response
            : Array.isArray(response?.results)
                ? response.results
                : Array.isArray(response?.data)
                    ? response.data
                    : [];

        const categorySet = new Set<string>();
        rows.forEach((row: any) => {
            const raw = row?.category;
            if (typeof raw === 'string') {
                const normalized = raw.trim();
                if (normalized) categorySet.add(normalized);
            }
        });

        return Array.from(categorySet).sort((a, b) => a.localeCompare(b));
    }
};

